import { motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { LeftArrowIcon } from './icons'
import styles from './LoginPopup.module.scss'

interface CodeInputProps {
  onBack: () => void
  phoneNumber: string
  onCodeSubmit: (code: string) => void
  onResendCode?: () => void
  isEmail?: boolean
  digits?: number
}

const CodeInput: React.FC<CodeInputProps> = ({
  onBack,
  phoneNumber,
  onCodeSubmit,
  onResendCode,
  isEmail = false,
  digits = 5,
}) => {
  const [code, setCode] = useState<string[]>(new Array(digits).fill(''))
  const [timeLeft, setTimeLeft] = useState(59)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const startTimer = () => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  useEffect(() => {
    startTimer()

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Автоматично переходимо до наступного поля
    if (value && index < digits - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // Перевіряємо, чи всі поля заповнені
    if (newCode.every(digit => digit !== '') && newCode.join('').length === digits) {
      onCodeSubmit(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResendCode = () => {
    // Call the resend callback if provided
    if (onResendCode) {
      onResendCode()
    }

    // Reset timer and code
    setTimeLeft(59)
    setCode(new Array(digits).fill(''))
    inputRefs.current[0]?.focus()

    // Restart the timer
    startTimer()
  }

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length >= 9) {
      return `+38 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(
        8,
        10,
      )} ${cleaned.slice(10, 12)}`
    }
    return phone
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack}>
          <LeftArrowIcon />
        </button>
        <h2 className={styles.headerTitle}>Вхід</h2>
        <div className={styles.placeholder}></div>
      </div>

      <motion.div
        className={styles.wrapper}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className={styles.codeTitleSection}>
          <h1 className={styles.codeTitle}>Введи {digits}-ти значний код</h1>
          <p className={styles.phoneText}>
            {isEmail
              ? `Код надіслано на email ${phoneNumber}`
              : `Код надіслано на номер ${formatPhoneNumber(phoneNumber)}`}
          </p>
        </div>

        <div className={styles.codeInputsContainer}>
          <div className={styles.codeInputs}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]"
                maxLength={1}
                value={digit}
                onChange={e => handleInputChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                className={styles.codeInput}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            className={styles.resendButton}
            onClick={handleResendCode}
            disabled={timeLeft > 0}
          >
            Надіслати код повторно{' '}
            {timeLeft > 0 ? `00:${timeLeft.toString().padStart(2, '0')}` : ''}
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default CodeInput
